import type { Recipe } from '../types/recipe';
import type { RecipeLookupReason, RecipeLookupResult } from './recipeLookup';
import { normalizeCodeKey, normalizeDishKey } from './recipeCukcukSync';

type RecipeIndex = Map<string, Recipe[]>;

const add = (index: RecipeIndex, key: string, recipe: Recipe) => {
  if (!key) return;
  const matches = index.get(key) || [];
  if (!matches.includes(recipe)) index.set(key, [...matches, recipe]);
};

const unique = (matches: Recipe[] | undefined, reason: RecipeLookupReason): RecipeLookupResult | undefined =>
  matches?.length === 1 ? { recipe: matches[0] } : matches && matches.length > 1 ? { reason } : undefined;

export const createRecipeLookup = (recipes: Recipe[]) => {
  const exactIds: RecipeIndex = new Map();
  const normalizedIds: RecipeIndex = new Map();
  const normalizedCodes: RecipeIndex = new Map();
  const normalizedNames: RecipeIndex = new Map();

  recipes.forEach((recipe) => {
    const identifiers = [recipe.itemId, ...(recipe.itemIdAliases || [])];
    identifiers.forEach((identifier) => {
      const exact = identifier.trim();
      add(exactIds, exact, recipe);
      add(normalizedIds, normalizeDishKey(exact), recipe);
      add(normalizedCodes, normalizeCodeKey(exact), recipe);
    });
    add(normalizedNames, normalizeDishKey(recipe.itemName), recipe);
  });

  const resolve = (input: { itemId?: string; itemName?: string }): RecipeLookupResult => {
    const itemId = (input.itemId || '').trim();
    if (itemId) {
      const exact = unique(exactIds.get(itemId), 'ambiguous_alias');
      if (exact) return exact;

      const normalized = unique(normalizedIds.get(normalizeDishKey(itemId)), 'ambiguous_alias');
      if (normalized) return normalized;

      const code = normalizeCodeKey(itemId);
      const normalizedCode = unique(normalizedCodes.get(code), 'ambiguous_normalized_code');
      if (normalizedCode) return normalizedCode;
    }

    const name = normalizeDishKey(input.itemName);
    const normalizedName = unique(normalizedNames.get(name), 'ambiguous_normalized_name');
    if (normalizedName) return normalizedName;

    return { reason: 'not_found' };
  };

  return { resolve };
};
